import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ChevronLeft, Image, MapPinned, Trash2, X, Loader2,
    Upload, FileImage, ZoomIn, FileText, ExternalLink, Video
} from 'lucide-react';
import { clubsService } from '../../../services';
import './ManageClubs.css';
import VenueSubnav from './VenueSubnav';
import VenueAccessBanner from './VenueAccessBanner';

const ManageMedia = () => {
    const { clubId } = useParams();
    const navigate = useNavigate();
    const [club, setClub] = useState(null);
    const [photos, setPhotos] = useState([]);
    const [maps, setMaps] = useState([]);
    const [videos, setVideos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('photos');
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef(null);

    // Upload form state
    const [uploadData, setUploadData] = useState({
        file: null,
        description: '',
        preview: null,
        videoUrl: ''
    });

    const fetchClubDetails = useCallback(async () => {
        try {
            const data = await clubsService.getClub(clubId);
            setClub(data);
        } catch (err) {
            console.error('Failed to fetch club details:', err);
        }
    }, [clubId]);

    const fetchMedia = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const clubData = await clubsService.getClub(clubId);
            setClub(clubData);
            setPhotos(Array.isArray(clubData?.photos) ? clubData.photos : []);
            setMaps(Array.isArray(clubData?.maps) ? clubData.maps : []);
            setVideos(Array.isArray(clubData?.videos) ? clubData.videos : []);
        } catch (err) {
            console.error(err);
            setError('Failed to load media. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    }, [clubId]);

    useEffect(() => {
        fetchClubDetails();
        fetchMedia();
    }, [fetchClubDetails, fetchMedia]);

    const isPremiumVenue = Boolean(club?.isPremiumVenue || (club?.profileTier || '').toLowerCase() === 'premium');
    const openUpgrade = () => navigate(`/dashboard/club/${clubId}/plan`);

    const isPdfFile = (fileOrUrl) => {
        if (!fileOrUrl) return false;
        if (typeof fileOrUrl === 'string') {
            return fileOrUrl.toLowerCase().split('?')[0].endsWith('.pdf');
        }

        return fileOrUrl.type?.toLowerCase() === 'application/pdf'
            || fileOrUrl.name?.toLowerCase().endsWith('.pdf');
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            const isMapTab = activeTab === 'maps';
            const isImage = file.type.startsWith('image/');
            const isPdf = isPdfFile(file);
            const isAllowed = isMapTab ? (isImage || isPdf) : isImage;

            if (!isAllowed) {
                setError(isMapTab
                    ? 'Please select an image or PDF file'
                    : 'Please select an image file');
                return;
            }

            if (file.size > 10 * 1024 * 1024) { // 10MB limit
                setError('File size must be less than 10MB');
                return;
            }

            if (isPdf) {
                setUploadData({
                    ...uploadData,
                    file,
                    preview: null
                });
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setUploadData({
                    ...uploadData,
                    file,
                    preview: reader.result
                });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleUpload = async () => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);

        try {
            if (activeTab === 'videos') {
                if (!uploadData.videoUrl.trim()) {
                    setError('Please enter a video URL');
                    setIsUploading(false);
                    return;
                }

                await clubsService.addClubVideo(clubId, {
                    videoUrl: uploadData.videoUrl.trim(),
                    description: uploadData.description
                });
            } else {
                if (!uploadData.file) {
                    setError('Please select a file to upload');
                    setIsUploading(false);
                    return;
                }

                const formData = new FormData();
                formData.append('file', uploadData.file);
                formData.append('description', uploadData.description);

                await (activeTab === 'photos'
                    ? clubsService.uploadClubPhoto(clubId, formData)
                    : clubsService.uploadClubMap(clubId, formData));
            }

            await fetchMedia();
            setShowUploadModal(false);
            resetUploadForm();
        } catch (err) {
            console.error('Failed to upload:', err);
            setError(err.message || 'Failed to upload. Please try again.');
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    const handleDelete = async (item, type) => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        const itemName = type === 'photo' ? 'photo' : type === 'map' ? 'map' : 'video';
        if (!window.confirm(`Are you sure you want to delete this ${itemName}? This action cannot be undone.`)) {
            return;
        }

        try {
            await (type === 'photo'
                ? clubsService.deleteClubPhoto(clubId, item.clubPhotoId)
                : type === 'map'
                    ? clubsService.deleteClubMap(clubId, item.clubMapId)
                    : clubsService.deleteClubVideo(clubId, item.clubVideoId));
            await fetchMedia();
        } catch (err) {
            console.error(`Failed to delete ${itemName}:`, err);
            setError(err.message || `Failed to delete ${itemName}. Please try again.`);
        }
    };

    const resetUploadForm = () => {
        setUploadData({
            file: null,
            description: '',
            preview: null,
            videoUrl: ''
        });
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const openPreview = (imageUrl) => {
        setPreviewImage(imageUrl);
        setShowPreviewModal(true);
    };

    const currentItems = activeTab === 'photos' ? photos : activeTab === 'maps' ? maps : videos;

    if (isLoading) {
        return (
            <div className="manage-clubs-page">
                <div className="manage-clubs-loading">
                    <div className="manage-clubs-spinner"></div>
                    <p>Loading media...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="manage-clubs-page">
            {/* Back Button */}
            <button 
                className="club-dashboard-back"
                onClick={() => navigate(`/dashboard/club/${clubId}/dashboard`)}
            >
                <ChevronLeft size={18} />
                Back to {club?.name || 'Venue'} Venue Dashboard
            </button>

            <VenueSubnav clubId={clubId} />

            <VenueAccessBanner club={club} clubId={clubId} navigate={navigate} premiumOnlyFeature featureLabel="Maps & photos" />

            {/* Header */}
            <div className="manage-section-header">
                <div className="manage-section-header-content">
                    <div className="manage-section-header-icon icon-purple">
                        <Image size={24} />
                    </div>
                    <div>
                        <h1>Maps, Photos & Videos</h1>
                        <p>{photos.length} photo{photos.length !== 1 ? 's' : ''}, {maps.length} map{maps.length !== 1 ? 's' : ''}, {videos.length} video{videos.length !== 1 ? 's' : ''}</p>
                    </div>
                </div>
                <button 
                    className="manage-section-add-btn"
                    onClick={() => setShowUploadModal(true)}
                    disabled={!isPremiumVenue}
                >
                    <Upload size={18} />
                    {isPremiumVenue ? (activeTab === 'videos' ? 'Add Video' : `Upload ${activeTab === 'photos' ? 'Photo' : 'Map'}`) : 'Upgrade For Media'}
                </button>
            </div>

            {!isPremiumVenue ? (
                <div className="venue-premium-lock-note">
                    Maps, photos, and videos are Premium venue tools. Free venues can review the section here, then upgrade from the Venue Plan page when they are ready to publish media.
                </div>
            ) : null}

            {error && (
                <div className="manage-clubs-error">
                    {error}
                    <button onClick={() => setError(null)} className="error-dismiss">
                        <X size={16} />
                    </button>
                </div>
            )}

            {/* Tabs */}
            <div className="media-tabs">
                <button 
                    className={`media-tab ${activeTab === 'photos' ? 'active' : ''}`}
                    onClick={() => setActiveTab('photos')}
                >
                    <FileImage size={18} />
                    Photos ({photos.length})
                </button>
                <button 
                    className={`media-tab ${activeTab === 'maps' ? 'active' : ''}`}
                    onClick={() => setActiveTab('maps')}
                >
                    <MapPinned size={18} />
                    Maps ({maps.length})
                </button>
                <button 
                    className={`media-tab ${activeTab === 'videos' ? 'active' : ''}`}
                    onClick={() => setActiveTab('videos')}
                >
                    <Video size={18} />
                    Videos ({videos.length})
                </button>
            </div>

            {/* Media Grid */}
            {currentItems.length === 0 ? (
                <div className="manage-clubs-empty">
                    <div className="manage-clubs-empty-icon">
                        {activeTab === 'photos' ? <FileImage size={48} /> : activeTab === 'maps' ? <MapPinned size={48} /> : <Video size={48} />}
                    </div>
                    <h3>No {activeTab === 'photos' ? 'Photos' : activeTab === 'maps' ? 'Maps' : 'Videos'} Yet</h3>
                    <p>{activeTab === 'videos' ? 'Add your first video link to show the venue in action.' : `Upload your first ${activeTab === 'photos' ? 'photo' : 'map'} to showcase your venue`}</p>
                    <button 
                        className="btn-primary"
                        onClick={() => setShowUploadModal(true)}
                        disabled={!isPremiumVenue}
                        style={{ marginTop: '16px' }}
                    >
                        <Upload size={18} />
                        {isPremiumVenue ? (activeTab === 'videos' ? 'Add Video' : `Upload ${activeTab === 'photos' ? 'Photo' : 'Map'}`) : 'Upgrade For Media'}
                    </button>
                </div>
            ) : (
                <div className="media-grid">
                    {currentItems.map((item) => {
                        const imageUrl = activeTab === 'photos' ? item.photoUrl : activeTab === 'maps' ? item.mapUrl : item.videoUrl;
                        const itemId = activeTab === 'photos' ? item.clubPhotoId : activeTab === 'maps' ? item.clubMapId : item.clubVideoId;
                        const isPdf = activeTab === 'maps' && isPdfFile(imageUrl);
                        return (
                            <div key={itemId} className="media-card">
                                <div className="media-card-image">
                                    {activeTab === 'videos' ? (
                                        <div className="media-pdf-preview">
                                            <Video size={42} />
                                            <span>Video link</span>
                                        </div>
                                    ) : isPdf ? (
                                        <div className="media-pdf-preview">
                                            <FileText size={42} />
                                            <span>PDF map</span>
                                        </div>
                                    ) : (
                                        <img src={imageUrl} alt={item.description || 'Media'} />
                                    )}
                                    <div className="media-card-overlay">
                                        {activeTab === 'videos' ? (
                                            <a
                                                className="media-overlay-btn"
                                                href={imageUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                title="Open video"
                                            >
                                                <ExternalLink size={20} />
                                            </a>
                                        ) : !isPdf ? (
                                            <button
                                                className="media-overlay-btn"
                                                onClick={() => openPreview(imageUrl)}
                                                title="View Full Size"
                                            >
                                                <ZoomIn size={20} />
                                            </button>
                                        ) : (
                                            <a
                                                className="media-overlay-btn"
                                                href={imageUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                title="Open PDF"
                                            >
                                                <ExternalLink size={20} />
                                            </a>
                                        )}
                                        <button 
                                            className="media-overlay-btn danger"
                                            onClick={() => handleDelete(item, activeTab === 'photos' ? 'photo' : activeTab === 'maps' ? 'map' : 'video')}
                                            title="Delete"
                                            disabled={!isPremiumVenue}
                                        >
                                            <Trash2 size={20} />
                                        </button>
                                    </div>
                                </div>
                                <div className="media-card-description">
                                    {item.description || (activeTab === 'videos' ? imageUrl : '')}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Upload Modal */}
            {showUploadModal && (
                <div className="modal-overlay" onClick={() => { setShowUploadModal(false); resetUploadForm(); }}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{activeTab === 'videos' ? 'Add Video' : `Upload ${activeTab === 'photos' ? 'Photo' : 'Map'}`}</h2>
                            <button className="modal-close" onClick={() => { setShowUploadModal(false); resetUploadForm(); }}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            {activeTab === 'videos' ? (
                                <div className="form-group">
                                    <label>Video URL</label>
                                    <input
                                        type="url"
                                        placeholder="https://youtube.com/... or hosted video URL"
                                        value={uploadData.videoUrl}
                                        onChange={(e) => setUploadData({ ...uploadData, videoUrl: e.target.value })}
                                    />
                                </div>
                            ) : (
                                <div className="upload-dropzone" onClick={() => fileInputRef.current?.click()}>
                                    {uploadData.preview ? (
                                        <div className="upload-preview">
                                            <img src={uploadData.preview} alt="Preview" />
                                            <button 
                                                className="upload-preview-remove"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    resetUploadForm();
                                                }}
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="upload-placeholder">
                                            <Upload size={40} />
                                            <p>Click to select a file</p>
                                            <span>{activeTab === 'photos' ? 'JPG, PNG, GIF, WEBP up to 10MB' : 'JPG, PNG, GIF, WEBP, PDF up to 10MB'}</span>
                                        </div>
                                    )}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept={activeTab === 'photos' ? 'image/*' : 'image/*,.pdf,application/pdf'}
                                        onChange={handleFileSelect}
                                        style={{ display: 'none' }}
                                    />
                                </div>
                            )}

                            <div className="form-group">
                                <label>Description (Optional)</label>
                                <input
                                    type="text"
                                    placeholder={`Add a description for this ${activeTab === 'photos' ? 'photo' : activeTab === 'maps' ? 'map' : 'video'}...`}
                                    value={uploadData.description}
                                    onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                                />
                            </div>

                            {isUploading && (
                                <div className="upload-progress">
                                    <div className="upload-progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn-secondary"
                                onClick={() => { setShowUploadModal(false); resetUploadForm(); }}
                                disabled={isUploading}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn-primary"
                                onClick={handleUpload}
                                disabled={(activeTab === 'videos' ? !uploadData.videoUrl.trim() : !uploadData.file) || isUploading}
                            >
                                {isUploading ? <Loader2 size={18} className="btn-spinner" /> : <Upload size={18} />}
                                {activeTab === 'videos' ? 'Add Video' : 'Upload'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Modal */}
            {showPreviewModal && previewImage && (
                <div className="modal-overlay preview-modal" onClick={() => setShowPreviewModal(false)}>
                    <div className="preview-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button 
                            className="preview-modal-close"
                            onClick={() => setShowPreviewModal(false)}
                        >
                            <X size={24} />
                        </button>
                        <img src={previewImage} alt="Full size preview" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageMedia;
